import { useEffect } from 'react';

interface ShortcutAction {
  key: string;
  action: () => void;
  description: string;
}

const defaultShortcuts: ShortcutAction[] = [
  {
    key: 'ctrl+k',
    action: () => {
      // Open command palette
      console.log('Opening command palette');
    },
    description: 'Open command palette'
  },
  {
    key: 'ctrl+l',
    action: () => {
      // Focus log search
      const logSearch = document.querySelector('input[placeholder*="logs"]') as HTMLInputElement;
      if (logSearch) {
        logSearch.focus();
      }
    },
    description: 'Focus log search'
  },
  {
    key: 'ctrl+shift+d',
    action: () => {
      // Toggle debug mode
      console.log('Toggling debug mode');
    },
    description: 'Toggle debug mode'
  },
  {
    key: 'ctrl+shift+p',
    action: () => {
      // Toggle performance profiler
      console.log('Toggling performance profiler');
    },
    description: 'Toggle performance profiler'
  },
  {
    key: 'ctrl+r',
    action: () => {
      // Refresh current view
      window.dispatchEvent(new CustomEvent('developer-refresh'));
    },
    description: 'Refresh current view'
  },
  {
    key: 'ctrl+s',
    action: () => {
      // Save current config
      console.log('Saving current configuration');
    },
    description: 'Save current configuration'
  },
  {
    key: 'ctrl+`',
    action: () => {
      // Open terminal
      console.log('Opening terminal');
    },
    description: 'Open terminal'
  }
];

const matchesShortcut = (e: KeyboardEvent, shortcut: string): boolean => {
  const parts = shortcut.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  
  const needsCtrl = parts.includes('ctrl');
  const needsShift = parts.includes('shift');
  const needsAlt = parts.includes('alt');
  const needsMeta = parts.includes('meta') || parts.includes('cmd');
  
  return (
    e.key.toLowerCase() === key &&
    e.ctrlKey === needsCtrl &&
    e.shiftKey === needsShift &&
    e.altKey === needsAlt &&
    e.metaKey === needsMeta
  );
};

export function useDeveloperShortcuts(customShortcuts: ShortcutAction[] = []) {
  const shortcuts = [...defaultShortcuts, ...customShortcuts];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        return;
      }

      shortcuts.forEach(shortcut => {
        if (matchesShortcut(e, shortcut.key)) {
          e.preventDefault();
          shortcut.action();
        }
      });
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);

  return {
    shortcuts,
    addShortcut: (shortcut: ShortcutAction) => {
      // This would typically update a state that manages shortcuts
      console.log('Adding shortcut:', shortcut);
    },
    removeShortcut: (key: string) => {
      // This would typically remove a shortcut from state
      console.log('Removing shortcut:', key);
    }
  };
}